import React, {useState} from 'react';
import {useLocation} from "react-router-dom";
import { useForm } from 'react-hook-form';
import ReactModal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useSystemMessage } from '../../../hooks/useSystemMessage';
import { tasklistApi } from '../../../apis/tasklist/tasklistApi';
import icon303 from '../../../assets/images/ROOM_TaskBoard_Re3_03.png';
import icon05 from '../../../assets/images/ROOM_TaskFile_Re_05.png';
import {ApiClientError} from "../../../apis/errors";
import {ValidationErrorMessages} from "../common/ValidationErrorMessages";
import {logError} from "../../../libs/utils/logError";
import useAppRouter from "../../../hooks/useAppRouter";

interface AddListModalProps {
  onSubmitted: () => Promise<void>;
}

interface FormValues {
  name: string;
}

export const AddListModal = (props: AddListModalProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    reValidateMode: 'onSubmit',
  });
  const { onSubmitted } = props;
  const { success } = useSystemMessage();
  const location = useLocation();
  const { goto } = useAppRouter();
  const [showModal, setShowModal] = React.useState<boolean>(false);

  const [ apiClientError, setApiClientError ] = useState<ApiClientError | null>(null);

  const onSubmit = async (data: FormValues) => {
    setApiClientError(null);
    try {
      await tasklistApi.create({ listName: data.name }, {});
      await onSubmitted();
      await success('リストを作成しました。');
      setShowModal(false);
      reset();
    } catch(e) {
      await logError(
        location.pathname,
        'タスクリストの作成に失敗しました。',
        e
      );

      if (e instanceof ApiClientError) {
        setApiClientError(e);
        return;
      }

      goto('internalServerError');
    }
  };

  return (
    <>
      <div className="rounded-t-lg bg-primary">
        <button
          type="button"
          className="flex items-center justify-center h-8 w-full text-white outline-none focus:outline-none"
          onClick={() => setShowModal(true)}
        >
          <img src={icon303} alt="icon" />
          <div className="ml-4 text-sm">リスト追加</div>
        </button>
      </div>
      <ReactModal
        isOpen={showModal}
        ariaHideApp={false}
        onRequestClose={() => setShowModal(false)}
        className="content"
        overlayClassName="overlay"
        shouldCloseOnOverlayClick
      >
        <div className="form-modal-container">
          <div className="form-modal-title">
            <h1 className="font-semibold">新規リスト追加</h1>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setShowModal(false)}
            >
              <img src={icon05} alt="icon" />
            </button>
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="form-modal-content">
              <div className="text-danger text-xs mb-4">
                { apiClientError?.message }
                { (Object.keys(errors).length > 0) && '入力エラーがあります' }
              </div>
              <input
                /* eslint-disable-next-line react/jsx-props-no-spreading */
                {...register('name', { required: true, maxLength: 100 })}
                placeholder="新規リスト名を入力してください"
                className="text-field"
              />
              {errors.name && errors.name.type === 'required' && (
                <span className="my-2 text-red-500 text-xs">
                  リスト名は必須です
                </span>
              )}
              {errors.name && errors.name.type === 'maxLength' && (
                <span className="my-2 text-red-500 text-xs">
                  リスト名は100文字以下で指定してください
                </span>
              )}
              <ValidationErrorMessages
                {...{
                  fieldErrors: apiClientError?.errors,
                  field: 'listName',
                }}
              />
            </div>
            <div className="form-modal-footer-container">
              <div className="form-modal-footer">
                <button
                  className="btn-sub"
                  type="button"
                  onClick={() => setShowModal(false)}
                >
                  キャンセル
                </button>
                <button
                  className="btn-primary"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin mr-4"
                      />
                      保 存
                    </>
                  ) : (
                    '保　存'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </ReactModal>
    </>
  );
};
